import { useCallback, useEffect, useRef, useState } from 'react';

import { useCustomToast } from './useCustomToast';

const DEFAULT_DEBOUNCE_MS = 300;

const sameIds = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id) => right.includes(id));

interface UseDebouncedIdSetPersistenceParams {
  initialIds: string[];
  onOptimisticChange: (newIds: string[]) => void;
  persist: (params: {
    currentIds: string[];
    savedIds: string[];
  }) => Promise<void>;
  onPersistSuccess?: (params: {
    currentIds: string[];
    savedIds: string[];
  }) => void;
  onPersistError: (revertIds: string[]) => void;
  errorToastMessage: string;
  debounceMs?: number;
}

interface UseDebouncedIdSetPersistenceResult {
  selectedIds: string[];
  handleToggle: (id: string) => void;
  isSaving: boolean;
}

export function useDebouncedIdSetPersistence({
  initialIds,
  onOptimisticChange,
  persist,
  onPersistSuccess,
  onPersistError,
  errorToastMessage,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDebouncedIdSetPersistenceParams): UseDebouncedIdSetPersistenceResult {
  const { errorMessage } = useCustomToast();

  const [selectedIds, setSelectedIds] = useState<string[]>(initialIds);
  const [isSaving, setIsSaving] = useState(false);

  const selectedIdsRef = useRef<string[]>(initialIds);
  const lastSavedIdsRef = useRef<string[]>(initialIds);
  const initialIdsRef = useRef<string[]>(initialIds);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const needsSaveRef = useRef(false);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (sameIds(initialIdsRef.current, initialIds)) {
      return;
    }

    initialIdsRef.current = initialIds;

    // pending-write guard: skip if a debounce or save is in flight
    if (debounceTimerRef.current !== null || savingRef.current) {
      return;
    }

    if (sameIds(selectedIdsRef.current, initialIds)) {
      lastSavedIdsRef.current = initialIds;
      return;
    }

    setSelectedIds(initialIds);
    selectedIdsRef.current = initialIds;
    lastSavedIdsRef.current = initialIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIds]);

  const flushToApi = useCallback(async () => {
    debounceTimerRef.current = null;

    if (savingRef.current) {
      needsSaveRef.current = true;
      return;
    }

    const currentIds = [...selectedIdsRef.current];
    const savedIds = [...lastSavedIdsRef.current];

    if (sameIds(currentIds, savedIds)) {
      return;
    }

    savingRef.current = true;
    needsSaveRef.current = false;
    setIsSaving(true);

    try {
      await persist({ currentIds, savedIds });
      onPersistSuccess?.({ currentIds, savedIds });
      lastSavedIdsRef.current = currentIds;
    } catch {
      const revertIds = lastSavedIdsRef.current;
      setSelectedIds(revertIds);
      selectedIdsRef.current = revertIds;
      onPersistError(revertIds);
      needsSaveRef.current = false;
      errorMessage(errorToastMessage);
    } finally {
      savingRef.current = false;

      const shouldFlushAgain =
        needsSaveRef.current ||
        !sameIds(selectedIdsRef.current, lastSavedIdsRef.current);

      if (shouldFlushAgain) {
        needsSaveRef.current = false;
        await flushToApi();
      } else {
        setIsSaving(false);
      }
    }
  }, [
    persist,
    onPersistSuccess,
    onPersistError,
    errorMessage,
    errorToastMessage,
  ]);

  const handleToggle = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const isRemoving = prev.includes(id);
        const newIds = isRemoving
          ? prev.filter((existing) => existing !== id)
          : [...prev, id];

        selectedIdsRef.current = newIds;
        onOptimisticChange(newIds);

        return newIds;
      });

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(flushToApi, debounceMs);
    },
    [onOptimisticChange, flushToApi, debounceMs],
  );

  return { selectedIds, handleToggle, isSaving };
}
